import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useLoginMutation,
  useSelfResetPasswordMutation,
} from "../../store/api/authApi";
import { setCredentials } from "../../store/slices/authSlice";
import Logo from "../../assets/images/Logo.png";

const Login = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    showPassword: false,
  });
  const [error, setError] = useState("");
  // Set to true when the BE response on a failed login includes
  // `canSelfReset: true`. The BE only sets this for accounts flagged as
  // double-hash victims, so the affected student is the only person who
  // can act on it for that username.
  const [canSelfReset, setCanSelfReset] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetForm, setResetForm] = useState({
    firstName: "",
    lastName: "",
    newPassword: "",
    confirmPassword: "",
    showPassword: false,
  });
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [login, { isLoading }] = useLoginMutation();
  const [selfResetPassword, { isLoading: isResetting }] =
    useSelfResetPasswordMutation();

  const handleChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
  };

  const handleClickShowPassword = () => {
    setFormData({ ...formData, showPassword: !formData.showPassword });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCanSelfReset(false);

    // Basic validation
    if (!formData.username || !formData.password) {
      setError("Please enter both username and password");
      return;
    }

    try {
      const result = await login({
        username: formData.username,
        password: formData.password,
      }).unwrap();

      if (result?.success) {
        const { token, refreshToken, user } = result;
        dispatch(setCredentials({ user, token, refreshToken }));
        navigate("/");
      } else {
        setError(result?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.data?.message || "Login failed. Please try again.");
      // The BE attaches `canSelfReset: true` to the 401 body only when
      // this username matches a known double-hash victim AND the recovery
      // flag is enabled server-side. Show the recovery affordance only
      // when the BE explicitly says so.
      if (err.data?.canSelfReset) {
        setCanSelfReset(true);
      }
    }
  };

  const openResetDialog = () => {
    setResetForm({
      firstName: "",
      lastName: "",
      newPassword: "",
      confirmPassword: "",
      showPassword: false,
    });
    setResetError("");
    setResetSuccess("");
    setResetOpen(true);
  };

  const closeResetDialog = () => {
    if (isResetting) return;
    setResetOpen(false);
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError("");

    const { firstName, lastName, newPassword, confirmPassword } = resetForm;
    if (!firstName.trim() || !lastName.trim()) {
      setResetError("Please enter your first name and last name.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match.");
      return;
    }

    try {
      const result = await selfResetPassword({
        username: formData.username,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        newPassword,
      }).unwrap();

      if (result?.success) {
        setResetSuccess(
          result.message ||
            "Password reset. You can now log in with your new password."
        );
        // Pre-fill the new password into the login form so the user can
        // just click Sign In once the dialog closes.
        setFormData((f) => ({ ...f, password: newPassword }));
        // Clear the affordance — they've used their one shot.
        setCanSelfReset(false);
        // Clear the stale "Invalid credentials" alert from the failed
        // login that triggered this dialog, so the success banner is
        // the only thing visible.
        setError("");
        // Close the dialog so the success alert (rendered on the login
        // page) is actually visible to the user.
        setResetOpen(false);
      } else {
        setResetError(result?.message || "Reset failed. Please try again.");
      }
    } catch (err) {
      console.error("Self-reset error:", err);
      setResetError(
        err.data?.message ||
          "Reset failed. Please contact your teacher for help."
      );
    }
  };

  const loading = isLoading;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #fef8ec 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: 6,
            borderRadius: 4,
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            width: "100%",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 700,
                background:
                  "linear-gradient(135deg, #2196F3 0%, #1976D2 50%, #1565C0 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              }}
            >
              STUDY TOEIC WITH MR. S
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#64748b",
                fontWeight: 500,
                fontSize: "1.1rem",
              }}
            >
              Sign in to continue your learning journey
            </Typography>
            <Box
              component="img"
              src={Logo}
              alt="STUDY TOEIC WITH MR. S"
              sx={{
                width: 150,
                borderRadius: 10,
                objectFit: "cover",
                mt: 3,
                mx: "auto",
                boxShadow: "0 4px 20px rgba(33, 150, 243, 0.4)",
                backdropFilter: "blur(10px)",
              }}
            />
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                background: "rgba(244, 67, 54, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(244, 67, 54, 0.2)",
                color: "#d32f2f",
                fontWeight: 500,
                "& .MuiAlert-icon": {
                  color: "#d32f2f",
                },
              }}
              action={
                canSelfReset ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={openResetDialog}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Reset password
                  </Button>
                ) : null
              }
            >
              {error}
              {canSelfReset && (
                <Box sx={{ mt: 0.5, fontSize: "0.85rem", fontWeight: 400 }}>
                  Your account is eligible for a one-time password reset.
                </Box>
              )}
            </Alert>
          )}

          {resetSuccess && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 3,
              }}
            >
              {resetSuccess}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", mt: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange("username")}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={formData.showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange("password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                background: "linear-gradient(135deg, #2196F3 0%, #1976D2 50%, #1565C0 100%)",
                "&:hover": {
                  opacity: 0.9,
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
            </Button>
            <Typography
              variant="body2"
              align="center"
              sx={{
                mt: 2,
                color: "#64748b",
                fontSize: "0.9rem",
              }}
            >
              By signing in, you agree to our Terms of Service and Privacy Policy
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/*
        Self-service password reset dialog. Only reachable via the "Reset
        password" affordance, which is itself only shown when the BE has
        flagged the account as eligible. The endpoint will reject any
        request for an ineligible account, so this UI cannot be misused
        to take over an active student's login.
      */}
      <Dialog
        open={resetOpen}
        onClose={closeResetDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Reset your password
        </DialogTitle>
        <Box component="form" onSubmit={handleResetSubmit}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Set a new password for{" "}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formData.username || "your account"}
              </Box>
              . Enter your first name and last name (as registered with
              your teacher) to verify your identity.
            </DialogContentText>

            {resetError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {resetError}
              </Alert>
            )}

            <TextField
              autoFocus
              required
              fullWidth
              margin="dense"
              label="First name"
              value={resetForm.firstName}
              onChange={(e) =>
                setResetForm((f) => ({ ...f, firstName: e.target.value }))
              }
            />

            <TextField
              required
              fullWidth
              margin="dense"
              label="Last name"
              value={resetForm.lastName}
              onChange={(e) =>
                setResetForm((f) => ({ ...f, lastName: e.target.value }))
              }
            />

            <TextField
              required
              fullWidth
              margin="dense"
              label="New password"
              type={resetForm.showPassword ? "text" : "password"}
              value={resetForm.newPassword}
              onChange={(e) =>
                setResetForm((f) => ({ ...f, newPassword: e.target.value }))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setResetForm((f) => ({
                          ...f,
                          showPassword: !f.showPassword,
                        }))
                      }
                      edge="end"
                    >
                      {resetForm.showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="At least 8 characters."
            />

            <TextField
              required
              fullWidth
              margin="dense"
              label="Confirm new password"
              type={resetForm.showPassword ? "text" : "password"}
              value={resetForm.confirmPassword}
              onChange={(e) =>
                setResetForm((f) => ({
                  ...f,
                  confirmPassword: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeResetDialog} disabled={isResetting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isResetting}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {isResetting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Reset password"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Login;
